import { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';

import { Button, Skeleton, Stack, Text, cn } from '@eluve/components';

export type PdfPreviewProps = {
  /**
   * Must be a signed URL, not a blob: URL — pdf.js fetches via CSP
   * `connect-src`, which allows storage.googleapis.com but not blob:.
   */
  file: string;
  className?: string;
  /**
   * Render the AcroForm widget layer and tint each field, so reviewers can SEE
   * where generated fields landed. Off by default — generated widgets are
   * transparent, so without this the preview looks like the untouched page.
   */
  highlightFields?: boolean;
};

// Inner padding around the page; the page is rendered to fill the width minus this.
const PADDING = 12;

const PreviewSkeleton: React.FC = () => (
  <Stack align="center" justify="center" className="p-6">
    <Skeleton className="h-[420px] w-[320px]" />
  </Stack>
);

const PreviewError: React.FC = () => (
  <Stack align="center" justify="center" className="p-6">
    <Text size="xs" color="secondary">
      Couldn&apos;t preview this PDF.
    </Text>
  </Stack>
);

/**
 * Read-only paged preview. Renders the page at the container's width so the
 * canvas and (optional) annotation layer scale together and stay aligned.
 */
export const PdfPreview: React.FC<PdfPreviewProps> = ({
  file,
  className,
  highlightFields = false,
}) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasError, setHasError] = useState(false);
  const [width, setWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      // Round and only update on a real change — contentRect.width is
      // fractional, so unrounded values never repeat and would re-rasterize
      // the page canvas on every resize tick.
      const next = Math.round(entry.contentRect.width - PADDING * 2);
      setWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Stack gap={2} align="stretch">
      {numPages > 1 && (
        <Stack gap={3} justify="center" align="center" direction="horizontal">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            disabled={pageNumber <= 1}
          >
            ← Prev
          </Button>
          <Text inline size="xs" color="secondary">
            Page {pageNumber} of {numPages}
          </Text>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
            disabled={pageNumber >= numPages}
          >
            Next →
          </Button>
        </Stack>
      )}
      <div
        ref={containerRef}
        className={cn(
          'overflow-x-hidden overflow-y-auto rounded-lg border border-border-primary bg-bg-tertiary',
          className,
        )}
      >
        <Stack
          direction="horizontal"
          justify="center"
          style={{ padding: PADDING }}
          className={cn(
            // Tint each AcroForm widget so reviewers see where fields landed;
            // keep the layer non-interactive (read-only review).
            highlightFields &&
              '[&_.annotationLayer]:pointer-events-none [&_.annotationLayer_section]:rounded-xs [&_.annotationLayer_section]:bg-bg-indigo-subtle-primary/50 [&_.annotationLayer_section]:ring-1 [&_.annotationLayer_section]:ring-border-indigo-strong',
          )}
        >
          {hasError ? (
            <PreviewError />
          ) : !file || width <= 0 ? (
            <PreviewSkeleton />
          ) : (
            <Document
              file={file}
              loading={<PreviewSkeleton />}
              error={<PreviewError />}
              onLoadSuccess={({ numPages: loaded }) => {
                setNumPages(loaded);
                setPageNumber(1);
                setHasError(false);
              }}
              onLoadError={() => setHasError(true)}
            >
              <Page
                pageNumber={pageNumber}
                width={width}
                renderTextLayer={false}
                renderAnnotationLayer={highlightFields}
                renderForms={highlightFields}
                loading={<PreviewSkeleton />}
                error={<PreviewError />}
              />
            </Document>
          )}
        </Stack>
      </div>
    </Stack>
  );
};
